import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n';
import './styles/theme.scss';
import { timberTrackTheme } from './theme/timbertrack-theme';
import { MantineProvider } from '@mantine/core';
import { BrowserRouter } from 'react-router-dom';

// Suppress Chrome ResizeObserver bug - harmless error that doesn't affect functionality
// Reference: https://crbug.com/809574
(() => {
  const re = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;
  const originalError = console.error;
  const originalWarn = console.warn;
  
  console.error = (...args: unknown[]) => {
    const msg = args?.[0]?.toString?.() || '';
    if (typeof msg === 'string' && re.test(msg)) return;
    originalError.apply(console, args);
  };
  
  console.warn = (...args: unknown[]) => {
    const msg = args?.[0]?.toString?.() || '';
    if (typeof msg === 'string' && re.test(msg)) return;
    originalWarn.apply(console, args);
  };
  
  // Suppress both error events and unhandledrejection events with capture phase
  window.addEventListener('error', (e) => {
    if (e.message && re.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, { capture: true });
  
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason?.toString?.() || '';
    if (typeof reason === 'string' && re.test(reason)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, { capture: true });
  
  // Override ResizeObserver to catch errors at source
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (e) {
            if (e instanceof Error && re.test(e.message)) {
              return;
            }
            throw e;
          }
        });
      });
    }
  };
})();

// Initialize theme before React renders to avoid FOUC
(() => {
  try {
    const storageKey = 'tt-theme';
    const saved = localStorage.getItem(storageKey);
    const systemPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    const theme = (saved === 'light' || saved === 'dark') ? saved : (systemPrefersDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', theme);
  } catch {
    // no-op
  }
})();

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = ReactDOM.createRoot(container);

root.render(
  <MantineProvider theme={timberTrackTheme} defaultColorScheme="light">
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </MantineProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
